import { z } from 'zod';
import { paginationSchema, sortOrderSchema } from './helpers';
import { knownTotalParamsSchema, knownTotalQuerySchema } from './known-total';
import { AI_INSTRUCTION_CATEGORIES, } from './platform-registry';
import { ColumnDataTypes, FiltersSchema, TableResponseMetaSchema, createTableQuerySchema, } from './table';
/**
 * Basic user info for AI instruction users list
 */
export const aiInstructionUserBasicSchema = z.object({
    email: z.string().nullable(),
    name: z.string().nullable(),
});
/**
 * AI Instruction item in the legacy list response (uses counts)
 */
export const aiInstructionItemSchema = z.object({
    contentId: z.string().uuid(),
    name: z.string().nullable(),
    summary: z.string().nullable(),
    filePath: z.string(),
    filePaths: z.array(z.string()),
    fileCategory: z.string().nullable(),
    idePlatform: z.string().nullable(),
    userCount: z.number(),
    projectCount: z.number(),
    firstSeenAt: z.string(),
    lastSeenAt: z.string(),
});
/**
 * AI Instruction item in the DataTable response (uses full arrays)
 */
export const aiInstructionDataTableItemSchema = z.object({
    contentId: z.string().uuid(),
    name: z.string().nullable(),
    summary: z.string().nullable(),
    filePath: z.string(),
    filePaths: z.array(z.string()),
    fileCategory: z.string().nullable(),
    idePlatform: z.string().nullable(),
    users: z.array(aiInstructionUserBasicSchema),
    projects: z.array(z.string()),
    /**
     * True when Kirin took a preventive action (quarantine) on this instruction
     * file — derived from a `prevented` finding on one of the row's file paths
     * (KIR-3937). Quarantine of an instruction/skill file empties it on disk, so
     * without this flag the row is indistinguishable from a genuinely empty file.
     * Label only: a lookup failure degrades to `false`, never to an error.
     */
    quarantinedByKirin: z.boolean(),
    firstSeenAt: z.string(),
    lastSeenAt: z.string(),
});
/**
 * List AI instructions response (legacy format)
 */
export const listAiInstructionsResponseSchema = z.object({
    instructions: z.array(aiInstructionItemSchema),
    totalCount: z.number(),
    page: z.number(),
    pageSize: z.number(),
});
/**
 * AI instruction content response
 */
export const aiInstructionContentResponseSchema = z.object({
    content: z.string(),
    generatedName: z.string().nullable(),
    generatedSummary: z.string().nullable(),
    contentHash: z.string(),
});
/**
 * AI instruction user item
 */
export const aiInstructionUserSchema = z.object({
    userId: z.string().uuid(),
    userName: z.string().nullable(),
    userEmail: z.string().nullable(),
    workspaceFolders: z.array(z.string()),
    lastSeenAt: z.string(),
});
/**
 * List AI instruction users response
 */
export const listAiInstructionUsersResponseSchema = z.object({
    users: z.array(aiInstructionUserSchema),
});
/**
 * AI instruction project item
 */
export const aiInstructionProjectSchema = z.object({
    projectId: z.string().uuid(),
    name: z.string(),
    slug: z.string(),
    userCount: z.number(),
    lastSeenAt: z.string(),
});
/**
 * List AI instruction projects response
 */
export const listAiInstructionProjectsResponseSchema = z.object({
    projects: z.array(aiInstructionProjectSchema),
});
// ============================================================================
// DataTable-specific schemas
// ============================================================================
/**
 * Filterable fields for the AI instructions DataTable.
 */
export const AI_INSTRUCTION_FILTERABLE_FIELDS = {
    NAME: 'name',
    TYPE: 'type',
    FILE: 'file',
    SUMMARY: 'summary',
    PROJECTS: 'projects',
    USERS: 'users',
};
/**
 * Fields backed by a server-side filter-options endpoint (name, file, projects, users).
 * type uses static options; summary uses text input.
 */
export const AI_INSTRUCTION_FILTER_OPTION_FIELDS = {
    NAME: 'name',
    FILE: 'file',
    PROJECTS: 'projects',
    USERS: 'users',
};
export const aiInstructionFilterOptionFieldSchema = z.enum([
    'name',
    'file',
    'projects',
    'users',
]);
/**
 * AI instruction types for DataTable filtering / display. Dictionary form of
 * `AI_INSTRUCTION_CATEGORIES` for typo-safe consumer access (e.g.
 * `AI_INSTRUCTION_TYPES.CONFIGURATION`). The dict is derived from the
 * canonical tuple in `platform-registry`, so adding a member there
 * automatically extends this dict at compile time. KIR-2595.
 */
export const AI_INSTRUCTION_TYPES = Object.fromEntries(AI_INSTRUCTION_CATEGORIES.map(c => [c.toUpperCase(), c]));
/**
 * Column data types for AI instruction DataTable fields.
 * Used by createTableQuerySchema to validate operator × datatype combinations.
 */
export const AI_INSTRUCTION_COLUMN_DATA_TYPES = {
    name: ColumnDataTypes.STRING,
    type: ColumnDataTypes.ENUM,
    file: ColumnDataTypes.STRING,
    summary: ColumnDataTypes.TEXT,
    projects: ColumnDataTypes.ENUM,
    users: ColumnDataTypes.ENUM,
};
/**
 * Sort fields for the AI instructions DataTable.
 */
export const aiInstructionDataTableSortFieldSchema = z.enum([
    'name',
    'type',
    'file',
    'summary',
    'projects',
    'users',
]);
/**
 * Backend query schema for the AI instructions DataTable.
 * Handles bracket-notation filter params from Fastify OR a pre-parsed filters array.
 */
const aiInstructionDataTableQueryBaseSchema = paginationSchema.extend({
    sortBy: aiInstructionDataTableSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    search: z.string().optional(),
    knownTotal: knownTotalQuerySchema,
});
export const aiInstructionDataTableQuerySchema = createTableQuerySchema(aiInstructionDataTableQueryBaseSchema, AI_INSTRUCTION_COLUMN_DATA_TYPES);
/**
 * Response schema for the AI instructions DataTable endpoint.
 * Shape: { data: AiInstructionDataTableItem[], pagination: {...}, meta: {...} }
 */
export const aiInstructionTableResponseSchema = z.object({
    data: z.array(aiInstructionDataTableItemSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
    meta: TableResponseMetaSchema,
});
/**
 * Query schema for fetching AI instruction filter options.
 */
export const aiInstructionFilterOptionsQuerySchema = z.object({
    field: aiInstructionFilterOptionFieldSchema,
    search: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(50).default(10),
    offset: z.coerce.number().int().min(0).default(0),
});
/**
 * User filter option with metadata (for the users filter dropdown)
 */
export const aiInstructionUserFilterOptionSchema = z.object({
    value: z.string(),
    label: z.string(),
    name: z.string().nullable(),
    email: z.string().nullable(),
});
/**
 * Response schema for AI instruction filter options.
 */
export const aiInstructionFilterOptionsResponseSchema = z.object({
    field: aiInstructionFilterOptionFieldSchema,
    options: z.array(z.string()),
    optionsWithMetadata: z.array(aiInstructionUserFilterOptionSchema).optional(),
    pagination: z.object({
        total: z.number().int().min(0),
        limit: z.number().int().min(1),
        offset: z.number().int().min(0),
        hasMore: z.boolean(),
        nextOffset: z.number().int().min(0).nullable().optional(),
    }),
});
/**
 * API client params for the AI instructions DataTable endpoint.
 * page and limit are optional here; the backend applies defaults.
 */
export const listAiInstructionsDataTableParamsSchema = z.object({
    page: z.number().int().min(1).optional(),
    limit: z.number().int().min(1).max(100).optional(),
    sortBy: aiInstructionDataTableSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    search: z.string().optional(),
    filters: z.array(FiltersSchema.element).optional(),
    knownTotal: knownTotalParamsSchema,
});
//# sourceMappingURL=ai-instructions.js.map